import { ConsequenceNote } from '@cwos/ui-admin';
import { SettingsField } from '../settings/common.js';
import { location } from '@cwos/api-client';
import {
  FLOOR_LABELS,
  ORGANIZATION_PENDING_HEADING,
  organizationPendingNote,
  PENDING_CHOICE_LEGEND,
  PENDING_HEADING,
  PENDING_REASON_HINT,
  PENDING_REASON_LABEL,
  savedInForceFor,
  SAVED_SCHEDULE_HEADING,
  type PolicyAudience,
  SCHEDULE_AT_FIELD_LABEL,
  SCHEDULE_AT_LABEL,
  SCHEDULE_IMMEDIATE_LABEL,
  SCHEDULE_LEGEND,
  SCHEDULE_PAST_NOTE,
  TIMELINE_HEADING,
  TIMELINE_NOTHING_UPCOMING,
  browserTimeZone,
  floorLabel,
  formatScheduleInstant,
  pendingChoicePrompt,
  pendingRelationshipLabel,
  savedGovernsFrom,
  scheduleZoneSentence,
  timelineUpcomingNote,
  timelineWithdrewNote,
} from './copy.js';
import { scheduledInstantOf, validateDraft, type PolicyForm } from './policyForm.js';

/**
 * WHEN A POLICY SAVE BEGINS TO GOVERN — the control the founder's ruling of
 * 2026-09-08 has needed since the server started honouring it.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS IS ON EVERY POLICY PAGE AND NOT ON ONE
 * ═══════════════════════════════════════════════════════════════════════════
 * All four hub pages that can save (General, Location tracking, Clock rules,
 * Privacy & retention) share `usePolicyForm` and each sends the WHOLE
 * `location_policy` body. So each of them makes a schedule decision whether it
 * shows one or not: `scheduledEffectiveAt` is `null` on every body this form
 * produces unless a human said otherwise, and `null` CLEARS a stored schedule.
 * A page that saved without showing this control would silently un-schedule a
 * change the operator set on another page. There is one control because there
 * is one act.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * TWO FLOORS, AND THIS SCREEN STATES BOTH
 * ═══════════════════════════════════════════════════════════════════════════
 *   "An increase or mixed change governs at the LATER OF its scheduled
 *    effective timestamp and the worker's next eligible disclosed shift
 *    boundary."
 *
 * The date belongs to the PUBLISHER and is one instant for the whole
 * organization. The shift boundary belongs to each WORKER, is a different
 * instant for every one of them, and has not happened yet while their shift is
 * open — so it is named rather than dated. Everything rendered below that
 * reports a floor reports BOTH, from the server's own `waitingOn` list, in the
 * server's own order. A screen that printed only the date would be telling half
 * the truth to the person deciding whether to track other people's movements.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT THIS COMPONENT NEVER DOES
 * ═══════════════════════════════════════════════════════════════════════════
 * It does not poll (the transition happens because `now()` moved, not because
 * anything woke up), it does not compute a governing instant of its own (the
 * server sends `governsFrom`, `waitingOn` and `explanation`; re-deriving them
 * here would be a second opinion about what a worker is tracked with), and it
 * NEVER renders a scheduled change as though it were in force — `inForce` is a
 * field on the wire and the two branches below read it rather than inferring it
 * from a date comparison in the browser's clock.
 *
 * PROP DISCIPLINE, borrowed from `PolicyCard.breadcrumbsEnabled` and for the
 * same reason: `form` is the whole form and there is no default for any of it.
 * A caller that does not know whether something is pending must find out before
 * it renders this, because `undefined` (the answer has not arrived) and `null`
 * (the server says nothing is pending) are different claims and only one of
 * them may be shown as "nothing is scheduled".
 */
export function PolicySchedule({
  form,
  audience,
}: {
  form: PolicyForm;
  /**
   * WHOSE WORKERS THIS SAVE REACHES. Required, with no default, because the
   * only sentence below that names an audience — `SavedReport`'s in-force line
   * — is a claim about how many people a tracking change has just started
   * applying to. A defaulted "your organization" printed on a program editor
   * would be an over-claim in the one direction this surface must never be
   * wrong in.
   */
  audience: PolicyAudience;
}) {
  const draft = form.draft;
  if (!draft) return null;
  // The SAME arguments the save uses, so the errors rendered beside a control
  // are the errors that are actually holding the button. `undefined` here is
  // the read not having landed, and `validateDraft`'s default of `[]` would
  // quietly say "nothing is waiting" — so the block below refuses to render
  // choices rather than rendering none.
  const errors = validateDraft(draft, form.pendingRevisions ?? []);
  const instant = scheduledInstantOf(draft);
  const zone = browserTimeZone();

  return (
    <div style={{ display: 'grid', gap: 'var(--space-10)' }}>
      <InheritedPendingBlock inherited={form.inheritedPending} />
      <PendingBlock form={form} errors={errors} />

      <fieldset
        style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 'var(--space-8)' }}
      >
        <legend className="rn-card-title" style={{ padding: 0 }}>
          {SCHEDULE_LEGEND}
        </legend>

        {/*
          A RADIO AND NOT A CHECKBOX, because these are two acts rather than one
          act with an optional extra. `unscheduled` and `scheduled for this
          minute` produce the same governance today and are different evidence
          forever; the wire keeps them apart, the version series records which
          one happened, and this is the only place a human chooses.
        */}
        <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
          <input
            type="radio"
            name="location-policy-schedule"
            checked={draft.scheduleMode === 'immediate'}
            disabled={!form.canManage}
            onChange={() => form.set({ scheduleMode: 'immediate' })}
          />
          <span>{SCHEDULE_IMMEDIATE_LABEL}</span>
        </label>
        <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
          <input
            type="radio"
            name="location-policy-schedule"
            checked={draft.scheduleMode === 'at'}
            disabled={!form.canManage}
            onChange={() => form.set({ scheduleMode: 'at' })}
          />
          <span>{SCHEDULE_AT_LABEL}</span>
        </label>

        {draft.scheduleMode === 'at' ? (
          <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
            <SettingsField
              label={SCHEDULE_AT_FIELD_LABEL}
              hint={scheduleZoneSentence(zone)}
              error={errors.scheduledLocal}
            >
              <input
                type="datetime-local"
                value={draft.scheduledLocal}
                disabled={!form.canManage}
                onChange={(e) => form.set({ scheduledLocal: e.target.value })}
              />
            </SettingsField>
            {/*
              A PAST INSTANT IS ALLOWED AND SAID OUT LOUD. `governs_from` is
              generated as greatest(effective_at, scheduled_effective_at), so the
              database clamps it to the moment of recording — a publisher cannot
              backdate a disclosure into a shift that has already run. Refusing
              the input would refuse something the server accepts and keeps as
              evidence of intent; saying nothing would let the operator believe a
              tracking change applied to yesterday.
            */}
            {instant !== null && instant.getTime() < Date.now() ? (
              <ConsequenceNote text={SCHEDULE_PAST_NOTE} />
            ) : null}
          </div>
        ) : null}
      </fieldset>

      <SavedReport schedule={form.saveSchedule} audience={audience} />
      <TimelineReport timeline={form.saveTimeline} />
    </div>
  );
}

/**
 * A CHANGE COMING FROM A LAYER ABOVE — SHOWN, AND NOT ASKED ABOUT.
 *
 * This is the third item of the founder's REVREL follow-up ruling made
 * structural: `GET /location/policy/effective` distinguishes `pendingRevisions`
 * (waiting on the scope a save would WRITE) from `pending` (the next change
 * that alters what governs HERE, which may belong to a layer above), and a
 * program editor that collapsed the two would render the ORGANIZATION's
 * scheduled revision as its own.
 *
 * That is not a cosmetic error. It would put four relationship radios under a
 * revision this form does not write; choosing one sends a statement naming a
 * version that is not in the program scope's live set, and the server answers
 * `NOT_PENDING` — a refusal the operator cannot act on from this screen, about
 * a revision they can see on it. So the block below has NO CHOICES at all, and
 * says whose the change is.
 *
 * `undefined` renders nothing (the read has not landed and this screen may not
 * claim there is no such change); `null` renders nothing because the server
 * answered and there genuinely is none. Two states, one render, kept apart in
 * the type for the day one of them acquires a sentence.
 */
function InheritedPendingBlock({ inherited }: { inherited: PolicyForm['inheritedPending'] }) {
  if (!inherited) return null;
  return (
    <div className="rn-card" role="note" style={{ display: 'grid', gap: 'var(--space-6)' }}>
      <strong>{ORGANIZATION_PENDING_HEADING}</strong>
      <p className="rn-muted" style={{ margin: 0 }}>
        {organizationPendingNote(formatScheduleInstant(inherited.governsFrom))}
      </p>
      {/*
        THE SERVER'S OWN SENTENCE, exactly as the per-scope block renders it —
        `explanation` names which authority is holding the change, and one
        wording for one fact is the rule this whole surface is built on.
      */}
      <p className="rn-muted" style={{ margin: 0 }}>
        {inherited.explanation}
      </p>
      <WaitingOn waitingOn={inherited.waitingOn} />
    </div>
  );
}

/**
 * WHAT IS ALREADY COMING, and what saving now does to it.
 *
 * `undefined` renders NOTHING — the read has not landed and "no change is
 * scheduled" is a claim this component is not entitled to make yet. `null`
 * renders nothing either, but for the opposite reason: the server answered and
 * there is genuinely nothing pending, so there is nothing to say. The two are
 * kept apart in the type even though they render the same, because the day one
 * of them acquires a sentence the other must not inherit it.
 */
function PendingBlock({
  form,
  errors,
}: {
  form: PolicyForm;
  errors: ReturnType<typeof validateDraft>;
}) {
  const revisions = form.pendingRevisions;
  // UNDEFINED RENDERS NOTHING and the save button is already disabled for the
  // same reason: the read has not landed, and "no change is scheduled" is a
  // claim this component is not entitled to make yet. An EMPTY ARRAY renders
  // nothing too, but for the opposite reason — the server answered and there is
  // genuinely nothing waiting.
  if (!revisions || revisions.length === 0) return null;
  return (
    <>
      {revisions.map((revision) => (
        <PendingRevisionChoice
          key={revision.versionId}
          form={form}
          revision={revision}
          error={errors.pendingChoices?.[revision.versionId]}
        />
      ))}
    </>
  );
}

/**
 * ONE WAITING REVISION, AND THE CHOICE THE RULING REQUIRES ABOUT IT.
 *
 * NO RADIO IS PRE-SELECTED. That is the whole component: the founder's ruling
 * of 2026-09-08 says the operator must be shown the pending revision and
 * EXPLICITLY choose, and a pre-selected option is this screen choosing. The
 * conservative-looking pre-selection ("keep" — leave the schedule alone) is not
 * even the safe one; it leaves a revision the operator may have forgotten
 * standing to arrive next month and replace the values they are saving now.
 *
 * The four labels come from a map keyed by the SERVER's closed vocabulary, and
 * an unrecognised key renders as an explicit unknown rather than being dropped
 * — a missing option reads as an option that does not exist, which on this
 * screen would mean an operator could not cancel a tracking increase because a
 * constant went stale.
 */
function PendingRevisionChoice({
  form,
  revision,
  error,
}: {
  form: PolicyForm;
  revision: location.PendingPolicyChange;
  error: string | undefined;
}) {
  const when = formatScheduleInstant(revision.governsFrom);
  const choice = form.draft?.pendingChoices[revision.versionId];
  const withdraws = choice?.relationship === 'supersede' || choice?.relationship === 'cancel';
  return (
    <div className="rn-card" role="note" style={{ display: 'grid', gap: 'var(--space-6)' }}>
      <strong>{PENDING_HEADING}</strong>
      <p className="rn-muted" style={{ margin: 0 }}>
        {savedGovernsFrom(when)}
      </p>
      {/*
        THE SERVER'S OWN SENTENCE, not one assembled here. `explanation` names
        which authority is holding the change, and it is the same string the
        worker-facing binding read answers with — one wording for one fact.
      */}
      <p className="rn-muted" style={{ margin: 0 }}>
        {revision.explanation}
      </p>
      <WaitingOn waitingOn={revision.waitingOn} />

      <fieldset
        style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 'var(--space-6)' }}
      >
        <legend className="rn-card-title" style={{ padding: 0 }}>
          {PENDING_CHOICE_LEGEND}
        </legend>
        <p className="rn-muted" style={{ margin: 0 }}>
          {pendingChoicePrompt(when)}
        </p>
        {location.PENDING_RELATIONSHIPS.map((relationship) => (
          <label
            key={relationship}
            style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'flex-start' }}
          >
            <input
              type="radio"
              name={`pending-${revision.versionId}`}
              checked={choice?.relationship === relationship}
              disabled={!form.canManage}
              onChange={() => form.choosePending(revision.versionId, { relationship })}
            />
            <span>{pendingRelationshipLabel(relationship)}</span>
          </label>
        ))}
        {withdraws ? (
          <SettingsField label={PENDING_REASON_LABEL} hint={PENDING_REASON_HINT} error={error}>
            <input
              type="text"
              value={choice?.reason ?? ''}
              disabled={!form.canManage}
              onChange={(e) => form.choosePending(revision.versionId, { reason: e.target.value })}
            />
          </SettingsField>
        ) : error ? (
          <p role="alert" className="rn-muted" style={{ margin: 0 }}>
            {error}
          </p>
        ) : null}
      </fieldset>
    </div>
  );
}

/**
 * THE RESULTING GOVERNING TIMELINE — the ruling's own phrase, rendered.
 *
 * Read from the server's answer and never re-derived here. An operator who
 * superseded a revision and one who kept it need different sentences, and only
 * the transaction that did it knows which happened; a browser re-deriving it
 * from the values on screen would be guessing about what a worker is tracked
 * with.
 *
 * `upcoming: []` gets a SENTENCE and not an empty render, for the same reason
 * `WaitingOn` prints a line for an empty list: "nothing else is scheduled" is
 * the answer to the question, and dropping it makes "nothing is coming" and "we
 * did not look" render identically.
 */
function TimelineReport({ timeline }: { timeline: PolicyForm['saveTimeline'] }) {
  if (!timeline) return null;
  return (
    <div className="rn-card" role="status" style={{ display: 'grid', gap: 'var(--space-6)' }}>
      <strong>{TIMELINE_HEADING}</strong>
      {timeline.withdrawn.map((w) => (
        <p key={w.versionId} className="rn-muted" style={{ margin: 0 }}>
          {timelineWithdrewNote(w.kind, formatScheduleInstant(w.wouldHaveGovernedFrom))}
        </p>
      ))}
      {timeline.upcoming.length === 0 ? (
        <p className="rn-muted" style={{ margin: 0 }}>
          {TIMELINE_NOTHING_UPCOMING}
        </p>
      ) : (
        timeline.upcoming.map((u) => (
          <p key={u.versionId} className="rn-muted" style={{ margin: 0 }}>
            {timelineUpcomingNote(formatScheduleInstant(u.governsFrom))}
          </p>
        ))
      )}
    </div>
  );
}

/**
 * THE REPORT A SAVE PRODUCED, read from the server rather than from the draft.
 *
 * The two branches are `inForce`, which is a field on the wire. It is not a
 * date comparison made here: the browser's clock is not the database's, and a
 * few hundred milliseconds of skew across that boundary would render a change
 * that IS in force as pending, or worse the other way.
 */
function SavedReport({
  schedule,
  audience,
}: {
  schedule: PolicyForm['saveSchedule'];
  audience: PolicyAudience;
}) {
  if (!schedule) return null;
  return (
    <div className="rn-card" role="status" style={{ display: 'grid', gap: 'var(--space-6)' }}>
      <strong>{SAVED_SCHEDULE_HEADING}</strong>
      <p className="rn-muted" style={{ margin: 0 }}>
        {schedule.inForce
          ? savedInForceFor(audience)
          : savedGovernsFrom(formatScheduleInstant(schedule.governsFrom))}
      </p>
      <p className="rn-muted" style={{ margin: 0 }}>
        {schedule.explanation}
      </p>
      <WaitingOn waitingOn={schedule.waitingOn} />
    </div>
  );
}

/**
 * EVERY FLOOR STILL IN THE WAY, one line each, in the server's order.
 *
 * An EMPTY list is not an empty render: "nothing is holding this back" is the
 * answer to the question, and dropping the line for it would make "in force"
 * and "we did not check" look identical. `floorLabel` renders an unrecognised
 * floor key as an explicit unknown rather than skipping it — a floor silently
 * omitted reads as a floor that has been crossed, which is the one direction
 * this surface must never be wrong in.
 */
function WaitingOn({ waitingOn }: { waitingOn: readonly string[] }) {
  if (waitingOn.length === 0) {
    return (
      <p className="rn-muted" style={{ margin: 0 }}>
        Nothing is holding it back.
      </p>
    );
  }
  return (
    <ul className="rn-muted" style={{ margin: 0, paddingLeft: 'var(--space-16)' }}>
      {waitingOn.map((floor) => (
        <li key={floor}>Waiting on {floorLabel(floor)}.</li>
      ))}
    </ul>
  );
}

/** Re-exported so a guard can assert the vocabulary is closed and covered. */
export const RENDERED_FLOORS = Object.keys(FLOOR_LABELS);
